/**
 * Serving the Global Market view from World Bank monthly averages when the
 * exchange feed has nothing.
 *
 * Farmgate used to show ICE and CBOT daily settlements on those pages. That
 * was a paid vendor feed whose trial expired, so `global_commodity_prices` is
 * empty in production and every card, chart, stat tile and table that reads
 * it renders blank. The World Bank Pink Sheet covers all five commodities we
 * list, free and under CC BY 4.0, so it fills the gap.
 *
 * It is NOT the same data, and the difference is the whole point of this
 * file. A monthly average is not a daily settlement. It has no open, high,
 * low or volume. It is published about a month in arrears. It is quoted per
 * tonne or per kilo, not per bushel or per pound. Any page showing it must
 * say so, and must drop the exchange bylines and contract symbols, because
 * "CC · ICE" above a World Bank figure is a false attribution and a CC BY
 * breach besides.
 *
 * The shape trick: `toPriceSeries` returns rows in exactly the shape the
 * exchange feed used, so the chart, the statistics and the table keep working
 * unchanged. The fields a monthly average does not have are left UNDEFINED,
 * never zero. Zero is a price; absent is not, and the pages already know how
 * to render absent as a dash.
 */

/** The exchange-traded commodities. Mirrors GLOBAL_TO_SERIES in the
 *  backend's world_reference.py. Used to map a card back to its series while
 *  the exchange feed is what is showing. */
export const WORLD_BACKED_GLOBAL_IDS = ['GC001', 'GC002', 'GC003', 'GC004', 'GC005'];

/**
 * The series that get a CARD, in the order they appear.
 *
 * NOT the five exchange contracts. Those were Cocoa, Soybeans, Soymeal,
 * Coffee and Sugar, which is the ICE and CBOT list we inherited from a feed
 * that expired, and only ONE of them, soybeans, is a crop Farmgate's
 * surveyors actually price. Soymeal, coffee and sugar are not grown or
 * tracked by the people using this.
 *
 * These are the crops the local data speaks to, most-surveyed first: maize
 * (5 local variants), rice (4), groundnuts (3), soybeans (2). Cocoa earns
 * the last slot as Nigeria's largest agricultural export even though no
 * field agent prices it.
 *
 * Sorghum is deliberately absent despite 3 local variants: the World Bank
 * retired that series in 2020, and a card is a headline, which is the wrong
 * place for a figure that stopped six years ago. It stays in the picker,
 * marked, where the warning travels with it.
 *
 * Five, not twenty two. A card is an overview; the picker beside the chart
 * is how you reach the rest.
 */
export const WORLD_CARD_SERIES = [
  'WR-MAIZE',
  'WR-RICE-TH5',
  'WR-GROUNDNUTS',
  'WR-SOYBEANS',
  'WR-COCOA',
];

/** The series a page opens on. Deliberately the SAME as the first card, so
 *  the headline card and the chart below it agree on opening. */
export const DEFAULT_WORLD_SERIES = WORLD_CARD_SERIES[0];

/**
 * Time ranges for a MONTHLY series.
 *
 * The exchange ranges (1M, 3M, 6M, 1Y) are day counts built for a daily
 * feed. On monthly data they mostly do not work, and 1M is simply broken:
 * the newest figure is published about a month in arrears, so a 31 day
 * window excludes every point and the chart empties with no explanation.
 * Years are the unit that suits 800 months of history.
 */
export const WORLD_TIME_RANGES = [
  { label: '1Y', value: 366 },
  { label: '3Y', value: 1096 },
  { label: '5Y', value: 1827 },
  { label: '10Y', value: 3653 },
  { label: 'ALL', value: 99999 },
];

/** The range a world chart opens on. Five years shows a shape worth reading
 *  without burying the recent months. */
export const DEFAULT_WORLD_RANGE = 1827;

/** Display units offered for world data. Tonne leads because the numbers are
 *  the familiar magnitude there: the Pink Sheet quotes cocoa and coffee per
 *  kilo, so $5.95 is the same price a futures screen shows as $5,950. Every
 *  conversion here is exact, unlike a bushel, which needs a weight per crop
 *  and would bury an assumption inside a number read as a price. */
export const WORLD_UNITS = [
  { value: 'MT', label: 'per tonne', suffix: '/mt' },
  { value: 'kg', label: 'per kilogram', suffix: '/kg' },
  { value: 'lb', label: 'per pound', suffix: '/lb' },
];

/** Kilograms in one unit of however a series is published. Only two shapes
 *  occur in the Pink Sheet, and an unknown one returns null so the caller
 *  shows the figure as published rather than converting it wrongly. */
export function kgPerPublishedUnit(seriesUnit) {
  if (!seriesUnit) return null;
  const u = String(seriesUnit).toLowerCase();
  if (u.endsWith('/kg')) return 1;
  if (u.endsWith('/mt') || u.endsWith('/tonne') || u.endsWith('/t')) return 1000;
  return null;
}

/** How many months to request. Long enough for the ALL range to mean
 *  something on a monthly series without shipping the 1960s to a carousel. */
export const WORLD_MONTHS = 240;

/**
 * One commodity's monthly points, in the row shape the exchange feed used.
 *
 * `close` carries the monthly average because that is the field every
 * consumer already reads. open/high/low/volume are deliberately absent: the
 * figure is a single monthly average and inventing the other four would be
 * inventing prices.
 */
export function toPriceSeries(points) {
  return (points || []).map((p) => ({ date: p.period, close: p.price }));
}

/**
 * The whole batch response as a {commodityId: rows} map, the same shape as
 * `globalCommodityPrices`, so a page can substitute one for the other.
 */
export function toPriceMap(batch) {
  const out = {};
  const entries = batch?.commodities || {};
  Object.keys(entries).forEach((id) => {
    const rows = toPriceSeries(entries[id]?.points);
    if (rows.length) out[id] = rows;
  });
  return out;
}

/** The mapping-only response as {commodityId: seriesId}, so a card can find
 *  the series standing in for it. */
export function toMappedSeriesIds(catalogueResponse) {
  const out = {};
  const mapped = catalogueResponse?.mapped || {};
  Object.keys(mapped).forEach((cid) => { out[cid] = mapped[cid].seriesId; });
  return out;
}

/** The catalogue as picker options, grouped, order preserved. */
export function toPickerOptions(catalogueResponse) {
  return (catalogueResponse?.series || []).map((s) => ({
    id: s.id, label: s.label, unit: s.unit, group: s.group,
  }));
}

/** The CC BY credit the API sends with every world response. Taken from the
 *  response rather than written here, so the backend and the interface cannot
 *  drift apart on the wording of a licence obligation. */
export function attributionOf(batch) {
  return batch?.attribution || '';
}

/** {commodityId: {id, label, unit}} for the series behind each commodity. */
export function toSeriesMap(batch) {
  const out = {};
  const entries = batch?.commodities || {};
  Object.keys(entries).forEach((id) => {
    if (entries[id]?.series) out[id] = entries[id].series;
  });
  return out;
}

/**
 * Is the exchange feed genuinely absent?
 *
 * Absent means no rows at all, for any commodity. A feed that is merely
 * sparse, or missing one commodity, is still an exchange feed and must keep
 * its exchange labelling; swapping sources per commodity would put two
 * different kinds of price on one carousel with one heading over them.
 */
export function exchangeFeedIsEmpty(globalCommodityPrices) {
  const m = globalCommodityPrices || {};
  return Object.keys(m).every((id) => (m[id] || []).length === 0);
}

/** "2026-08-01" to "Aug 2026". The day is deliberately never shown: printing
 *  "1 Aug" would invite reading a monthly average as a daily observation. */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function monthLabel(iso) {
  if (!iso) return '-';
  const [y, m] = String(iso).split('-');
  const idx = Number(m) - 1;
  if (!y || Number.isNaN(idx) || !MONTHS[idx]) return '-';
  return `${MONTHS[idx]} ${y}`;
}

/**
 * A series label split for a card: the crop, and its qualifier.
 *
 * The World Bank names series "Crop, qualifier": "Rice, Thailand 5%",
 * "Coffee, arabica", "Sugar, world". On a card that whole string is too long
 * and truncates to "Rice, Tha...", which tells the reader nothing. Split at
 * the first comma and the two halves land naturally as a heading and the
 * line under it. A label with no comma keeps its group as the second line.
 */
export function cardLabel(label, group) {
  const i = String(label || '').indexOf(',');
  if (i === -1) return { name: label || '', detail: group || '' };
  return { name: label.slice(0, i).trim(), detail: label.slice(i + 1).trim() };
}

/** The unit as the reader should see it: the currency they chose, and the
 *  weight they chose. The Pink Sheet quotes dollars per kilo or per tonne;
 *  showing naira means the figure was multiplied by the exchange rate, so the
 *  symbol changes with it, and showing tonnes means it was multiplied by a
 *  thousand, so the denominator changes too. */
export function unitLabelFor(seriesUnit, currency, displayUnit) {
  if (!seriesUnit) return '';
  const symbol = currency === 'NGN' ? '\u20a6' : '$';
  const chosen = WORLD_UNITS.find((u) => u.value === displayUnit);
  // No display unit chosen, or a series published in something we cannot
  // convert: show it exactly as published.
  if (!chosen || kgPerPublishedUnit(seriesUnit) == null) {
    return currency === 'NGN' ? seriesUnit.replace(/^\$/, '\u20a6') : seriesUnit;
  }
  return `${symbol}${chosen.suffix}`;
}

/** The copy that has to travel with the data, in one place so the two pages
 *  cannot drift apart on what they claim. */
export const WORLD_COPY = {
  sectionTitle: 'World Reference Prices',
  eyebrow: 'World Reference',
  subtitle:
    'Monthly average world prices from the World Bank Pink Sheet, published about a month in arrears. Not exchange settlements.',
  cadenceNote: 'Monthly average, published about a month in arrears.',
  sourceLine: 'Source: World Bank Pink Sheet',
  statsHeading: 'Monthly Statistics',
  changeLabel: 'Change (month on month)',
  highLabel: 'Highest month',
  lowLabel: 'Lowest month',
  discontinuedLabel: 'Series discontinued',
  emptyTitle: 'World reference prices are loading',
  emptyBody:
    'Monthly averages from the World Bank Pink Sheet will populate this chart.',
};
